import type { BibleLocation } from '@skjv/common';
import { compareLocations } from '@skjv/common';

/** Apply study passage outline to verses in a chapter element */
export function applyStudyPassageToChapter(
  chapterElement: HTMLElement,
  studyPassageStart: BibleLocation | null,
  studyPassageEnd: BibleLocation | null,
) {
  if (!studyPassageStart) return;

  const start = studyPassageStart;
  const end = studyPassageEnd;

  // Determine the effective range
  let rangeStart: BibleLocation;
  let rangeEnd: BibleLocation;

  if (!end) {
    // No ending location — outline entire chapter of startingLocation
    rangeStart = { ...start, verseIndex: 0 };
    // Use a high verse index to capture all verses in the chapter
    rangeEnd = { ...start, verseIndex: 999 };
  } else {
    rangeStart = start;
    rangeEnd = end;
  }

  const verses = chapterElement.querySelectorAll('.verse');
  const matchingVerses: Element[] = [];

  verses.forEach((verseEl) => {
    const id = verseEl.id;
    if (!id) return;
    const parts = id.replace('v-', '').split('-').map(Number);
    if (parts.length < 4) return;

    const verseLoc: BibleLocation = {
      testamentIndex: parts[0],
      bookIndex: parts[1],
      chapterIndex: parts[2],
      verseIndex: parts[3],
    };

    // Check if this verse falls within the study passage range
    if (compareLocations(verseLoc, rangeStart) >= 0 && compareLocations(verseLoc, rangeEnd) <= 0) {
      verseEl.classList.add('study-passage');
      matchingVerses.push(verseEl);
    }
  });

  // Mark first and last verses in this chapter's portion
  if (matchingVerses.length > 0) {
    matchingVerses[0].classList.add('study-passage-first');
    matchingVerses[matchingVerses.length - 1].classList.add('study-passage-last');
  }
}

/** Remove all study passage outlines from a container */
export function clearStudyPassageOutline(container: HTMLElement) {
  container
    .querySelectorAll('.verse.study-passage')
    .forEach((el) => {
      el.classList.remove('study-passage', 'study-passage-first', 'study-passage-last');
    });
}

/** Scroll to the study passage and glow the entire outlined range */
export function scrollToStudyPassage(
  container: HTMLElement,
  location: BibleLocation,
  scrollAdjustmentUntilSetter: (until: number) => void,
) {
  const verseId = `v-${location.testamentIndex}-${location.bookIndex}-${location.chapterIndex}-${location.verseIndex}`;
  const verseElement = document.getElementById(verseId);

  if (!verseElement) return;

  // Block scroll-based chapter loading during programmatic scroll
  scrollAdjustmentUntilSetter(Date.now() + 2000);

  verseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // Wait for scroll to settle, then glow all study-passage verses
  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
        observer.disconnect();
        setTimeout(() => glowStudyPassage(container), 500);
      }
    },
    { root: container, threshold: [0.5] },
  );

  observer.observe(verseElement);

  // Fallback if observer doesn't fire
  setTimeout(() => {
    observer.disconnect();
    glowStudyPassage(container);
  }, 1500);
}

/** Add glow class to all study-passage verses, remove after animation */
function glowStudyPassage(container: HTMLElement) {
  const passages = container.querySelectorAll('.verse.study-passage');
  if (passages.length === 0) return;

  // Don't re-glow if already glowing
  if (passages[0].classList.contains('study-passage-glow')) return;

  passages.forEach((el) => el.classList.add('study-passage-glow'));

  // Remove glow after animation completes (3.5s + buffer)
  setTimeout(() => {
    container
      .querySelectorAll('.verse.study-passage-glow')
      .forEach((el) => el.classList.remove('study-passage-glow'));
  }, 4000);
}
